import {
  type SatelliteWebLandlordGetScreeningCriterionDto,
  ScreeningCriteriaType,
} from '@zg-rentals/ts-satellite-client';
import { getCriterion } from './formState';
import { CREDIT_SCORE_OPTIONS, INCOME_TO_RENT_MULTIPLIER, INCOME_TO_RENT_OPTIONS, NOT_APPLICABLE } from './constants';

type Option = { label: string; value: string };

export type ComplianceFromCriteriaResult = {
  creditAlert: SatelliteWebLandlordGetScreeningCriterionDto | undefined;
  incomeAlert: SatelliteWebLandlordGetScreeningCriterionDto | undefined;
  filteredIncomeOptions: Array<Option>;
  filteredCreditOptions: Array<Option>;
  /** When false, hide the income-to-rent form field (e.g. not applicable for this listing). */
  isIncomeFieldEnabled: boolean;
  /** When false, hide the credit score form field (e.g. not applicable for this listing). */
  isCreditFieldEnabled: boolean;
};

/** Filter income-to-rent options by listing max; returns all options when max is missing or invalid. */
function filterIncomeOptionsByMax(max: string | undefined): Array<Option> {
  if (max == null || max === '') return INCOME_TO_RENT_OPTIONS;
  const maxVal = parseFloat(max);
  if (Number.isNaN(maxVal)) return INCOME_TO_RENT_OPTIONS;
  return INCOME_TO_RENT_OPTIONS.filter((opt) => {
    if (opt.value === NOT_APPLICABLE) return true;
    const multiplier = INCOME_TO_RENT_MULTIPLIER[opt.value as string];
    return multiplier != null && multiplier <= maxVal;
  });
}

/** Filter credit score options by listing max; returns all options when max is missing or invalid. */
function filterCreditOptionsByMax(max: string | undefined): Array<Option> {
  if (max == null || max === '') return CREDIT_SCORE_OPTIONS;
  const maxVal = parseInt(max, 10);
  if (Number.isNaN(maxVal)) return CREDIT_SCORE_OPTIONS;
  return CREDIT_SCORE_OPTIONS.filter((opt) => {
    if (opt.value === NOT_APPLICABLE) return true;
    if (opt.value === '740+') return maxVal >= 740;
    const num = parseInt(opt.value as string, 10);
    return !Number.isNaN(num) && num <= maxVal;
  });
}

/** Build compliance alerts and filtered options from the get criteria response. */
export function buildComplianceFromCriteria(
  criteria: Array<SatelliteWebLandlordGetScreeningCriterionDto>,
): ComplianceFromCriteriaResult {
  const incomeCriterion = getCriterion(criteria, ScreeningCriteriaType.INCOME_TO_RENT_RATIO);
  const creditCriterion = getCriterion(criteria, ScreeningCriteriaType.CREDIT_SCORE_MIN);

  return {
    creditAlert: creditCriterion,
    incomeAlert: incomeCriterion,
    filteredIncomeOptions: filterIncomeOptionsByMax(incomeCriterion?.max),
    filteredCreditOptions: filterCreditOptionsByMax(creditCriterion?.max),
    isIncomeFieldEnabled: incomeCriterion?.isEnabled ?? true,
    isCreditFieldEnabled: creditCriterion?.isEnabled ?? true,
  };
}
